// src/SideWinder.Web/src/components/strategy/allocationModel.ts
//
// Pure helpers for the Strategy Allocation card (spec 2026-10-02 §6.1, §7). Type-only imports from
// api/client, so a component test that mocks '../api/client' is unaffected by this module.
import type { FundAllocation, FundAllocationsResponse } from '../../api/client'

export type AllocationModel = 'legacy' | 'shared_pot'

/** GET/PUT /api/user/fund-allocations: a bare array is the legacy model; the envelope is the shared pot. */
export function splitFundAllocations(resp: FundAllocationsResponse): { model: AllocationModel; rows: FundAllocation[] } {
  if (Array.isArray(resp)) return { model: 'legacy', rows: resp }
  return {
    model: resp?.allocationModel === 'shared_pot' ? 'shared_pot' : 'legacy',
    rows: resp?.allocations ?? [],
  }
}

/** `v-model.number` yields '' for an emptied input; the wire value for "no tier" is null. */
export function tierOrNull(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

/**
 * The first rule a shared-pot row set breaks, or null. Mirrors the server's
 * SharedPotAllocationRules.Validate (the server stays the authority): weight 0..100, tier a whole
 * number 1..9 and required on a row that is on with a weight, reserve 0..50. There is no sum rule.
 */
export function potRowsError(rows: FundAllocation[]): string | null {
  for (const r of rows) {
    const weight = Number(r.weightPct)
    if (!(weight >= 0 && weight <= 100)) return `${r.fund}: weight must be between 0 and 100`
    const tier = tierOrNull(r.tier)
    if (tier !== null && !(Number.isInteger(tier) && tier >= 1 && tier <= 9))
      return `${r.fund}: tier must be a whole number from 1 to 9`
    if (r.enabled && weight > 0 && tier === null) return `${r.fund}: set a tier (1 = filled first)`
    const reserve = Number(r.reservePct ?? 0)
    if (!(reserve >= 0 && reserve <= 50)) return `${r.fund}: reserve must be between 0 and 50`
  }
  return null
}

/**
 * Funds the caller actually allocates to: rows that are on with a weight above zero. Null when the
 * response carries no rows at all, so the caller treats the set as unknown and shows everything.
 */
export function allocatedFunds(resp: FundAllocationsResponse): Set<string> | null {
  const { rows } = splitFundAllocations(resp)
  if (rows.length === 0) return null
  return new Set(rows.filter(r => r.enabled && Number(r.weightPct) > 0).map(r => r.fund))
}
